import { Accordion, Badge, Text } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, Missing } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { FindProdSiteFieldDefsByOrgIdResponse } from "@tfg/types/schemas/modules/traceability/dtos/find-prod-site-field-defs-by-org-id.dto"

interface Props {
  handleDelete: (id: string) => void
  handleUpdate: (
    productionSiteFieldDefinition: FindProdSiteFieldDefsByOrgIdResponse[number]
  ) => void
  handleAddToProducts: (id: string) => void
}

const columnHelper =
  createColumnHelper<FindProdSiteFieldDefsByOrgIdResponse[number]>()
export const useColumns = ({
  handleDelete,
  handleUpdate,
  handleAddToProducts,
}: Props) => {
  const { t } = useTranslation("admin")
  return useMemo<
    ColumnDef<FindProdSiteFieldDefsByOrgIdResponse[number], any>[]
  >(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("forActivities", {
        header: "Activities",
        cell: ({ getValue }) => (
          <Accordion w={400}>
            <Accordion.Item value="activities">
              <Accordion.Control>
                <Text>
                  {t("prodSiteFieldDefs.columns.activitiesValue", {
                    length: getValue().length,
                  })}
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                {getValue().map((activityName: string) => (
                  <Badge key={activityName} c="gray">
                    {activityName}
                  </Badge>
                ))}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        ),
      }),
      columnHelper.accessor("enumValues", {
        header: "Enum Values",
        cell: ({ getValue }) => {
          const values = getValue()
          return values.length === 0 ? (
            <Missing />
          ) : (
            <Accordion w={400}>
              <Accordion.Item value="activities">
                <Accordion.Control>
                  <Text>
                    {t("prodSiteFieldDefs.columns.enumValues", {
                      length: getValue().length,
                    })}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  {getValue().map((activity: string) => (
                    <Badge key={activity} c="gray">
                      {activity}
                    </Badge>
                  ))}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )
        },
      }),
      columnHelper.accessor((row) => ({ min: row.min, max: row.max }), {
        header: "Min-Max",
        cell: ({ getValue }) => {
          const { min, max } = getValue()

          return min === null && max === null ? (
            <Missing />
          ) : (
            <Text>{`${min ?? ""}-${max ?? ""}`}</Text>
          )
        },
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "delete",
        cell: ({ row }) => (
          <ActionIcon
            type="delete"
            onClick={() => handleDelete(row.original.id)}
          />
        ),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "update",
        cell: ({ row }) => (
          <ActionIcon type="edit" onClick={() => handleUpdate(row.original)} />
        ),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "addToProducts",
        cell: ({ row }) => (
          <ActionIcon
            type="refresh"
            onClick={() => handleAddToProducts(row.original.id)}
            toolTip="Apply this production site field definition to organization products"
          />
        ),
        enableSorting: false,
      }),
    ],
    [handleDelete, handleUpdate, handleAddToProducts, t]
  )
}
